import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API } from '../App';
import { Star, Camera, CheckCircle2, Loader2, AlertCircle, MapPin, Building2 } from 'lucide-react';

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {[1, 2, 3, 4, 5].map(s => (
        <button
          key={s} type="button"
          onClick={() => onChange(s)}
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
          style={{
            fontSize: 36, background: 'none', border: 'none', cursor: 'pointer',
            color: (hover || value) >= s ? '#fbbf24' : '#e2e8f0',
            transition: 'color 0.1s, transform 0.1s',
            transform: (hover || value) >= s ? 'scale(1.1)' : 'scale(1)',
            lineHeight: 1, padding: '0 2px'
          }}
        >★</button>
      ))}
    </div>
  );
}

export default function AnketForm() {
  const { uuid } = useParams();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [cevaplar, setCevaplar] = useState({});
  const [adSoyad, setAdSoyad] = useState('');
  const [telefon, setTelefon] = useState('');
  const [fotograflar, setFotograflar] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    fetch(`${API}/anketler/public/form/${uuid}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); return; }
        setForm(data);
        // Init answers
        const init = {};
        data.sorular.forEach(s => {
          init[s.id] = { soru_id: s.id, yildiz_degeri: null, evet_hayir: null, acik_metin: '', emoji_degeri: null, secili_secenek: '' };
        });
        setCevaplar(init);
      })
      .catch(() => setError('Bağlantı hatası'));
  }, [uuid]);

  const setCevap = (id, field, val) => {
    setCevaplar(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  };

  const handleFileChange = e => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    setFotograflar(files);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // Validate zorunlu
    for (const s of form.sorular) {
      if (!s.zorunlu) continue;
      const c = cevaplar[s.id];
      if (s.soru_tipi === 'yildiz' && !c?.yildiz_degeri) return toast.error(`"${s.soru_metni}" sorusu zorunludur`);
      if (s.soru_tipi === 'emoji' && !c?.emoji_degeri) return toast.error(`"${s.soru_metni}" sorusu zorunludur`);
      if (s.soru_tipi === 'cok_secenekli' && !c?.secili_secenek) return toast.error(`"${s.soru_metni}" sorusu zorunludur`);
      if (s.soru_tipi === 'evet_hayir' && c?.evet_hayir === null) return toast.error(`"${s.soru_metni}" sorusu zorunludur`);
    }

    setSending(true);
    try {
      const fd = new FormData();
      fd.append('ad', adSoyad);
      fd.append('telefon', telefon);
      fd.append('cevaplar', JSON.stringify(Object.values(cevaplar)));
      fotograflar.forEach(f => fd.append('fotograflar', f));

      const r = await fetch(`${API}/anketler/public/gonder/${uuid}`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Hata');
      setDone(true);
    } catch (err) {
      toast.error(err.message || 'Gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const logoUrl = form?.lokasyon?.kurum_logo
    ? `${API.replace('/api','')}/uploads/${form.lokasyon.kurum_logo}`
    : null;

  if (done) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', padding: 20
    }}>
      <div style={{
        background: '#fff', borderRadius: 24, padding: 48,
        textAlign: 'center', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'linear-gradient(135deg,#059669,#10b981)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px'
        }}>
          <CheckCircle2 size={36} color="#fff" />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>Teşekkürler!</h2>
        <p style={{ fontSize: 15, color: '#475569', margin: 0 }}>
          Değerlendirmeniz başarıyla alındı. Geri bildiriminiz için teşekkür ederiz.
        </p>
      </div>
    </div>
  );

  if (error) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg,#fef2f2,#fee2e2)', padding: 20
    }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 40, textAlign: 'center', maxWidth: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <AlertCircle size={48} color="#ef4444" style={{ margin: '0 auto 16px' }} />
        <h2 style={{ margin: '0 0 8px', color: '#0f172a' }}>Hata</h2>
        <p style={{ color: '#475569', margin: 0 }}>{error}</p>
      </div>
    </div>
  );

  if (!form) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <Loader2 size={36} style={{ animation: 'spin 0.7s linear infinite', color: 'var(--blue)' }} />
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
      padding: '20px 16px 40px'
    }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {/* Header card */}
        <div style={{
          background: 'linear-gradient(135deg,#0f172a,#1e3a5f)',
          borderRadius: 20, padding: 28, marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 16
        }}>
          {logoUrl
            ? <img src={logoUrl} alt="logo" style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover', background: '#fff', flexShrink: 0 }} />
            : <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Star size={28} color="#fbbf24" />
              </div>
          }
          <div>
            <h2 style={{ color: '#fff', fontWeight: 800, fontSize: 18, margin: '0 0 6px' }}>{form.anket?.baslik}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
              <Building2 size={13} />
              {form.lokasyon?.bina_adi && <span>{form.lokasyon.bina_adi} ·</span>}
              <MapPin size={13} />
              <span>{form.lokasyon?.lokasyon_adi}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Sorular */}
          {form.sorular.map((s, i) => (
            <div key={s.id} className="card" style={{ padding: 22, marginBottom: 14 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 14, color: 'var(--navy)' }}>
                <span style={{ color: 'var(--blue)', marginRight: 8 }}>{i + 1}.</span>
                {s.soru_metni}
                {s.zorunlu && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
              </div>

              {s.soru_tipi === 'yildiz' && (
                <div>
                  <StarRating
                    value={cevaplar[s.id]?.yildiz_degeri}
                    onChange={v => setCevap(s.id, 'yildiz_degeri', v)}
                  />
                  {cevaplar[s.id]?.yildiz_degeri && (
                    <p style={{ fontSize: 13, color: 'var(--gray-600)', marginTop: 8 }}>
                      {['', 'Çok Kötü', 'Kötü', 'Orta', 'İyi', 'Mükemmel'][cevaplar[s.id].yildiz_degeri]}
                    </p>
                  )}
                </div>
              )}

              {s.soru_tipi === 'evet_hayir' && (
                <div style={{ display: 'flex', gap: 12 }}>
                  {[{ val: true, label: 'Evet' }, { val: false, label: 'Hayır' }].map(opt => (
                    <button
                      key={String(opt.val)} type="button"
                      onClick={() => setCevap(s.id, 'evet_hayir', opt.val)}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 12,
                        border: `2px solid ${cevaplar[s.id]?.evet_hayir === opt.val ? (opt.val ? '#16a34a' : '#dc2626') : 'var(--gray-200)'}`,
                        background: cevaplar[s.id]?.evet_hayir === opt.val ? (opt.val ? '#f0fdf4' : '#fef2f2') : '#fff',
                        color: cevaplar[s.id]?.evet_hayir === opt.val ? (opt.val ? '#16a34a' : '#dc2626') : 'var(--gray-600)',
                        fontWeight: 600, fontSize: 15, cursor: 'pointer', transition: 'all 0.15s'
                      }}
                    >
                      {opt.val ? '✓ ' : '✗ '}{opt.label}
                    </button>
                  ))}
                </div>
              )}


              {s.soru_tipi === 'emoji' && (() => {
                const EMOJILER = ['😡','😞','😐','😊','😍'];
                const LBLS = ['Çok Kötü','Kötü','Orta','İyi','Mükemmel'];
                const val = cevaplar[s.id]?.emoji_degeri;
                return (
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {EMOJILER.map((em, i) => (
                      <button key={i} type="button"
                        onClick={() => setCevap(s.id, 'emoji_degeri', i + 1)}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                          padding: '10px 14px', borderRadius: 12, border: `2px solid ${val === i+1 ? '#2563eb' : 'var(--gray-200)'}`,
                          background: val === i+1 ? '#eff6ff' : '#fff', cursor: 'pointer',
                          transition: 'all 0.12s', flex: 1, minWidth: 56
                        }}>
                        <span style={{ fontSize: 32, lineHeight: 1, filter: val === i+1 ? 'none' : 'grayscale(0.4)', transform: val === i+1 ? 'scale(1.15)' : 'scale(1)', transition: 'all 0.12s' }}>{em}</span>
                        <span style={{ fontSize: 11, color: val === i+1 ? '#2563eb' : 'var(--gray-600)', fontWeight: val === i+1 ? 700 : 400 }}>{LBLS[i]}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}

              {s.soru_tipi === 'cok_secenekli' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(s.secenekler || []).length === 0 && (
                    <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>Seçenek tanımlanmamış</p>
                  )}
                  {(s.secenekler || []).map((secenek, si) => {
                    const secili = cevaplar[s.id]?.secili_secenek === secenek;
                    return (
                      <label key={si}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                          border: `2px solid ${secili ? '#2563eb' : 'var(--gray-200)'}`,
                          background: secili ? '#eff6ff' : '#fff',
                          transition: 'all 0.12s', userSelect: 'none'
                        }}>
                        <input
                          type="radio"
                          name={`soru_${s.id}`}
                          value={secenek}
                          checked={secili}
                          onChange={() => setCevap(s.id, 'secili_secenek', secenek)}
                          style={{ width: 18, height: 18, accentColor: '#2563eb', flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 14, fontWeight: secili ? 600 : 400, color: secili ? '#2563eb' : 'var(--gray-700)' }}>
                          {secenek}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {s.soru_tipi === 'acik_metin' && (
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Görüşünüzü yazın..."
                  value={cevaplar[s.id]?.acik_metin || ''}
                  onChange={e => setCevap(s.id, 'acik_metin', e.target.value)}
                  style={{ resize: 'vertical' }}
                />
              )}
            </div>
          ))}

          {/* Ad/Telefon - opsiyonel */}
          <div className="card" style={{ padding: 22, marginBottom: 14 }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600, color: 'var(--gray-600)' }}>Bilgileriniz (opsiyonel)</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input className="input" placeholder="Adınız Soyadınız" value={adSoyad} onChange={e => setAdSoyad(e.target.value)} />
              <input className="input" type="tel" placeholder="Telefon numaranız" value={telefon} onChange={e => setTelefon(e.target.value)} />
            </div>
          </div>

          {/* Fotoğraf */}
          <div className="card" style={{ padding: 22, marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--gray-600)' }}>Fotoğraf (opsiyonel, max 5)</h3>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: 10,
                border: '2px dashed var(--gray-200)', background: 'var(--gray-50)',
                color: 'var(--gray-600)', cursor: 'pointer', fontSize: 14,
                width: '100%', justifyContent: 'center'
              }}
            >
              <Camera size={16} />
              {fotograflar.length > 0 ? `${fotograflar.length} fotoğraf seçildi` : 'Fotoğraf Ekle'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleFileChange} />
          </div>

          <button
            type="submit" disabled={sending}
            style={{
              width: '100%', padding: '16px',
              background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
              border: 'none', borderRadius: 14, color: '#fff',
              fontSize: 16, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 16px rgba(59,130,246,0.35)',
              opacity: sending ? 0.7 : 1, fontFamily: 'inherit'
            }}
          >
            {sending ? <Loader2 size={20} style={{ animation: 'spin 0.7s linear infinite' }} /> : <CheckCircle2 size={20} />}
            {sending ? 'Gönderiliyor...' : 'Değerlendirmeyi Gönder'}
          </button>
        </form>
      </div>
    </div>
  );
}